import { Box, Group, createStyles } from '@mantine/core';
import { useArtifactPresignDownload } from '@repo/api-codegen';
import { Button, Text } from '@repo/foundations';
import { useEffect, useState } from 'react';
import { saveBlob } from '../../../../lib/models/misc';

const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		borderRadius: theme.other.borderRadius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		marginTop: theme.spacing.md,
		marginBottom: theme.spacing.md,
		background: theme.other.getColor('surface/primary/default'),
	},
	fileName: {
		fontWeight: 500,
		color: theme.other.getColor('text/primary/default'),
	},
	fileSize: {
		color: theme.other.getColor('text/secondary/default'),
	},
}));

interface ArtifactRendererProps {
	artifactId: string;
}

export function ArtifactRenderer({ artifactId }: ArtifactRendererProps) {
	const { classes } = useStyles();
	const [isDownloading, setIsDownloading] = useState(false);
	const [fileSize, setFileSize] = useState<number | null>(null);

	const { data: artifactData, isLoading } = useArtifactPresignDownload({
		queryParams: { artifact_id: artifactId },
	});

	// Get the real file size from the presigned URL
	useEffect(() => {
		if (!artifactData || !artifactData.url) return;

		const fetchFileSize = async () => {
			try {
				const response = await fetch(artifactData.url, { method: 'HEAD' });
				if (response.ok) {
					const contentLength = response.headers.get('Content-Length');
					if (contentLength) {
						setFileSize(parseInt(contentLength, 10));
					}
				}
			} catch (error) {
				// Failed to get file size
			}
		};

		fetchFileSize();
	}, [artifactData]);

	const downloadFile = async () => {
		if (!artifactData || !artifactData.url) return;

		setIsDownloading(true);
		try {
			// Fetch the file and create a blob
			const response = await fetch(artifactData.url);
			if (!response.ok) {
				throw new Error(`Failed to download file: ${response.statusText}`);
			}

			const blob = await response.blob();
			// Update the file size if we didn't get it from the HEAD request
			if (!fileSize && blob.size) {
				setFileSize(blob.size);
			}

			// Extract filename from URL or use a default name
			let fileName =
				artifactData.file_name || `artifact-${artifactId.slice(0, 8)}`;

			try {
				// Try to extract filename from URL if not provided
				if (!artifactData.file_name) {
					const url = new URL(artifactData.url);
					const urlFileName = url.pathname.split('/').pop();
					if (urlFileName) {
						fileName = decodeURIComponent(urlFileName);
					}

					// If there's a filename parameter in the URL, use that
					const fileNameParam = url.searchParams.get('original_name');
					if (fileNameParam) {
						fileName = fileNameParam;
					}
				}
			} catch (e) {
				// Ignore URL parsing errors
			}

			saveBlob(blob, fileName);
		} catch (error) {
			// Download failed
		} finally {
			setIsDownloading(false);
		}
	};

	if (isLoading || !artifactData) {
		return (
			<Box className={classes.container}>
				<Text>Loading artifact...</Text>
			</Box>
		);
	}

	return (
		<Box className={classes.container}>
			<Group position="apart">
				<Box>
					<Text className={classes.fileName}>{artifactData.file_name}</Text>
					{fileSize && (
						<Text size="sm" className={classes.fileSize}>
							{formatFileSize(fileSize)}
						</Text>
					)}
				</Box>
				<Button onClick={downloadFile} loading={isDownloading}>
					Download
				</Button>
			</Group>
		</Box>
	);
}

// Helper function to format file size
function formatFileSize(bytes: number): string {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
